import React, { useState } from 'react';

const VideoUpsellModal = ({ isOpen, onClose, onSelectVideo, listingId }) => {
  const [selectedPlan, setSelectedPlan] = useState('ai');

  const videoPlans = [
    {
      id: 'ai',
      title: 'AI Videó',
      price: 2990,
      features: [
        'Automatikus videó generálás',
        'Képekből készült slideshow',
        'Háttérzene',
        'HD minőség',
        '30-60 másodperc'
      ],
      preview: '🎬',
      color: 'from-blue-500 to-cyan-500',
      recommended: true
    },
    {
      id: 'pro',
      title: 'PRO Videó',
      price: 9990,
      features: [
        'Profi AI renderelés',
        'Cinematic átmenetek',
        'Szöveges feliratok',
        'Full HD minőség',
        '60-90 másodperc',
        'TikTok/Instagram optimalizált'
      ],
      preview: '🎥',
      color: 'from-purple-500 to-pink-500',
      recommended: false
    }
  ];

  const handleRequest = () => {
    const plan = videoPlans.find(p => p.id === selectedPlan);
    onSelectVideo(plan);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border-2 border-white/[0.08] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2">
                🎬 Mutassuk meg videón is!
              </h2>
              <p className="text-white/90">
                A videós hirdetések akár 3x több érdeklődőt hoznak
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Video Preview Placeholder */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 mb-6 text-center border-2 border-white/10">
            <div className="text-6xl mb-4">📹</div>
            <p className="text-gray-300 text-sm">
              Videó előnézet itt jelenik meg
            </p>
          </div>

          {/* Plans */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {videoPlans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative cursor-pointer rounded-2xl p-6 border-2 transition-all duration-300 ${
                  selectedPlan === plan.id
                    ? 'border-white scale-105 shadow-2xl'
                    : 'border-white/[0.08] hover:border-white/40'
                } ${plan.recommended ? 'ring-4 ring-yellow-400/50' : ''}`}
              >
                {/* Recommended Badge */}
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-black px-4 py-1 rounded-full shadow-lg">
                    NÉPSZERŰ
                  </div>
                )}

                {/* Radio Button */}
                <div className="absolute top-4 right-4">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.id ? 'border-white bg-white' : 'border-white/40'
                  }`}>
                    {selectedPlan === plan.id && (
                      <div className="w-3 h-3 rounded-full bg-purple-600" />
                    )}
                  </div>
                </div>

                {/* Plan Content */}
                <div className="text-center mb-4">
                  <div className="text-5xl mb-3">{plan.preview}</div>
                  <div className={`inline-block bg-gradient-to-r ${plan.color} text-white font-black text-sm px-4 py-1 rounded-full mb-3`}>
                    {plan.title}
                  </div>
                  <div className="text-3xl font-black text-white mb-1">
                    {plan.price.toLocaleString()} Ft
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="text-green-500">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Stats Box */}
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-xl rounded-xl p-4 border border-green-500/30 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📊</span>
              <div>
                <h4 className="text-white font-bold mb-1">Videó statisztikák</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-2xl font-black text-white">+280%</div>
                    <div className="text-gray-300">Több megtekintés</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black text-white">3x</div>
                    <div className="text-gray-300">Több érdeklődő</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-4 rounded-xl transition-colors border-2 border-white/[0.08]"
            >
              Most kihagyom
            </button>
            <button
              onClick={handleRequest}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-pink-600 hover:to-purple-600 text-white font-black py-4 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-purple-500/50 transition-all"
            >
              Kérem a videót! 🎬
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUpsellModal;
